import type { ComponentPropsWithoutRef } from "react";
import { addMinutes, format, isWithinInterval, subMinutes } from "date-fns";
import { t } from "ttag";

import type { Session } from "@/features/coaching/models";
import {
  SESSION_COLOR_MAP,
  SESSION_ICON_MAP,
} from "@/features/coaching/models";
import { JoinNowButton } from "@/features/consultations/components";
import { useBoolean } from "@/shared/hooks";
import { formatTime } from "@/shared/lib";
import {
  Button,
  ClockIcon,
  CloseCircleIcon,
  DetailCard,
  IconWrapper,
  UserCircleIcon,
} from "@/shared/ui";
import { tw } from "@/shared/utils";
import { UnsubscribeSessionModal } from "../unsubscribe-session-modal";

type SubscribedSessionCardProps = ComponentPropsWithoutRef<
  typeof DetailCard.Root
> & {
  session: Session;
};

export const SubscribedSessionCard = ({
  session,
  className,
  ...props
}: SubscribedSessionCardProps) => {
  const {
    value: showUnsubscribeSessionModal,
    setTrue: openUnsubscribeSessionModal,
    setFalse: closeUnsubscribeSessionModal,
  } = useBoolean(false);

  const now = new Date();

  const canJoin = isWithinInterval(now, {
    start: subMinutes(session.startAt, 10),
    end: addMinutes(session.startAt, session.duration),
  });

  return (
    <>
      <DetailCard.Root
        color={SESSION_COLOR_MAP[session.category]}
        className={tw("max-w-full gap-4 bg-white", className)}
        {...props}
      >
        <DetailCard.Header className="items-center">
          <IconWrapper size="sm">
            {SESSION_ICON_MAP[session.category]}
          </IconWrapper>
          <DetailCard.Title className="line-clamp-1">
            {session.title}
          </DetailCard.Title>
        </DetailCard.Header>

        <DetailCard.Content className="px-0">
          <div className="mt-auto flex flex-wrap gap-2 sm:flex-nowrap">
            <DetailCard.Badge color="light-primary">
              {session.startAt ? format(session.startAt, "MM/dd/yy") : "-"}
            </DetailCard.Badge>
            <DetailCard.Badge outline>
              {formatTime(session.startAt)}
            </DetailCard.Badge>
          </div>

          <div className="flex flex-row gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <UserCircleIcon className="size-3.5" />
              <DetailCard.Label>{t`Coach`}</DetailCard.Label>
              <DetailCard.Description className="truncate">
                {session.coach?.name ?? "-"}
              </DetailCard.Description>
            </div>

            <div className="flex items-center gap-1">
              <DetailCard.Description>
                {session.duration}
              </DetailCard.Description>
              <DetailCard.Label>{t`min`}</DetailCard.Label>
              <ClockIcon className="size-3.5" />
            </div>
          </div>
        </DetailCard.Content>

        <DetailCard.Footer className="gap-2 px-0">
          <JoinNowButton canJoin={canJoin} videoUrl={session.joinUrl ?? ""} />

          <Button
            className="h-auto px-4"
            variant="secondary"
            size="lg"
            color="red"
            onClick={openUnsubscribeSessionModal}
          >
            <CloseCircleIcon className="size-4 rotate-90" />
          </Button>
        </DetailCard.Footer>
      </DetailCard.Root>

      <UnsubscribeSessionModal
        show={showUnsubscribeSessionModal}
        onClose={closeUnsubscribeSessionModal}
        session={session}
      />
    </>
  );
};
